import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { Customer } from 'server/shared/models/customer';
import { Organisation } from 'server/shared/models/organisation';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser } from 'server/shared/models/user';
import { queryParam$ } from 'src/helpers/queryParam$';
import {
  urlEncodeMultiDaySelection,
  UserShiftSelectionForUrl,
} from '../../../helpers/job/urlEncodeMultiDaySelection';
import {
  MultiDaySelectionSummary,
  UserShiftSelection,
} from '../../../types/job/MultiDaySelectionSummary';
import { PoolerService } from '../../services/pooler/pooler.service';
import { AvailabilityManagementService } from './service/availability-management.service';
import { AvailabilitySelectionService } from './service/availability-selection.service';

@Component({
  selector: 'availability-management',
  templateUrl: 'availability-management.html',
  styleUrls: ['availability-management.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AvailabilityManagementService, AvailabilitySelectionService],
})
export class AvailabilityManagement {
  destroyed$ = new Subject<void>();
  searchText$ = new BehaviorSubject<string>('');
  userTracker?: Tracker.Computation;
  organisations$ = new BehaviorSubject<Organisation[]>([]);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  loggedInCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);

  departmentParam$ = queryParam$(this.route, 'department');

  selectionSummary$ = this.availabilitySelectionService.selectionSummary$;
  calendarTableHelper$ =
    this.availabilityManagementService.calendarTableHelper$;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private poolerService: PoolerService,
    public availabilityManagementService: AvailabilityManagementService,
    public availabilitySelectionService: AvailabilitySelectionService,
  ) {
    this.departmentParam$.subscribe((departmentId) => {
      this.applyDefaultFilters(departmentId);
    });
  }

  get searchText() {
    return this.searchText$.value;
  }

  set searchText(value: string) {
    this.searchText$.next(value);
  }

  get organisations() {
    return this.organisations$.value;
  }

  set organisations(value: Organisation[]) {
    this.organisations$.next(value);
  }

  get user() {
    return this.user$.value;
  }

  set user(value: DispoUser | undefined) {
    this.user$.next(value);
  }

  get loggedInCustomer() {
    return this.loggedInCustomer$.value;
  }

  set loggedInCustomer(value: Customer | undefined) {
    this.loggedInCustomer$.next(value);
  }

  ngOnInit() {
    this.userTracker = Tracker.autorun(() => {
      this.user = Users.findOne({
        _id: Meteor.userId(),
        role: RoleEnum.DISPO,
      }) as DispoUser;
      this.loggedInCustomer = Customers.findOne(this.user?.customerId);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.userTracker?.stop();
  }

  applyDefaultFilters(departmentId: string) {
    let query: any = {};
    if (departmentId) {
      query['departments.id'] = departmentId;
    }
    let defaults = this.loggedInCustomer?.defaultJobValues?.find(
      (defaults) => defaults.department.id === departmentId,
    );
    if (defaults) {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            certificates:
              defaults?.values?.certificates?.map((c) => c.id)?.join(',') || '',
            specialities:
              defaults?.values?.specialities?.map((s) => s.id)?.join(',') || '',
          },
          queryParamsHandling: 'merge',
        })
        .then();
    }
  }

  transformSelectionForUrlEncoding({
    date,
    shift,
    user,
    canBookDirectly,
  }: UserShiftSelection): UserShiftSelectionForUrl {
    return {
      date,
      userId: user._id,
      shiftId: shift.id,
    };
  }

  async submitSelection(
    selectionSummary: MultiDaySelectionSummary,
    // whether to book bookable users or just invite them
    inviteAll: boolean,
  ) {
    const inviteUsers = inviteAll
      ? [...selectionSummary.inviteUsers, ...selectionSummary.canBookUsers]
      : selectionSummary.inviteUsers;
    const bookUsers = inviteAll ? [] : selectionSummary.canBookUsers;
    const allUsers = [...inviteUsers, ...bookUsers];
    const certificates = Array.from(
      new Set(
        allUsers.reduce((acc: string[], user) => {
          return [...acc, ...(user.user.certificates?.map((c) => c.id) || [])];
        }, []),
      ),
    );
    const specialities = Array.from(
      new Set(
        allUsers.reduce((acc: string[], user) => {
          return [...acc, ...(user.user.specialities?.map((s) => s.id) || [])];
        }, []),
      ),
    );

    // Check if any selected users are from shared pool customers
    // customerOfUser is already available on AvailabilityListUser
    const hasExternalInvitees = allUsers.some((selection) => {
      const customerOfUser = selection.user.customerOfUser;
      return (
        customerOfUser && customerOfUser._id !== this.loggedInCustomer?._id
      );
    });

    const queryParams: Params = {
      departmentId: selectionSummary.departmentId,
      certificates: certificates.join(','),
      specialities: specialities.join(','),
      multiDaySelection: urlEncodeMultiDaySelection(
        inviteUsers.map(this.transformSelectionForUrlEncoding),
        bookUsers.map(this.transformSelectionForUrlEncoding),
      ),
    };

    if (hasExternalInvitees) {
      queryParams.allowExternalInvitees = 'true';
    }

    this.router
      .navigate(['jobs', 'new'], {
        queryParams,
      })
      .then();
  }
}
